import { useRef } from "react";
import { motion } from "framer-motion";
import { Photo, Reveal, Eyebrow, useParallax } from "./primitives";

// Mobile only (below md): eyebrow and title swap colors, with a soft halo so they read over bright sky.
export const SWAP_EYEBROW = "max-md:!text-ivory max-md:[text-shadow:0_0_12px_rgba(20,16,12,0.8),0_1px_2px_rgba(20,16,12,0.6)]";
export const SWAP_TITLE = "max-md:!text-[#F7BCA0] max-md:[text-shadow:0_0_18px_rgba(20,16,12,0.75),0_1px_2px_rgba(20,16,12,0.55)]";
// Soft blurred shade behind the text only on mobile; the photo itself is untouched.
export const MOBILE_GLOW = "pointer-events-none absolute -inset-x-6 -inset-y-6 -z-10 rounded-[48px] bg-[rgba(20,16,12,0.72)] blur-2xl md:hidden";

export default function PhotoBand({ img, eyebrow, title, text, note, testid, children }) {
  const ref = useRef(null);
  const y = useParallax(ref, ["-10%", "10%"]);
  return (
    <section ref={ref} data-testid={testid} className="relative flex min-h-[560px] items-end overflow-hidden bg-ink lg:h-[80vh] lg:items-center">
      <motion.div style={{ y }} className="absolute inset-x-0 -inset-y-[12%]">
        <Photo img={img} sizes="100vw" className="h-full w-full object-cover" />
      </motion.div>
      <div aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,rgba(28,24,20,0.85),rgba(28,24,20,0.2)_60%)] lg:bg-[linear-gradient(to_right,rgba(28,24,20,0.75),rgba(28,24,20,0.1)_65%)]" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-20 sm:px-8 lg:px-12">
        <div className="relative isolate max-w-2xl">
          <div aria-hidden className={MOBILE_GLOW} />
          {eyebrow && (
            <Reveal>
              <Eyebrow tone="dark" className={SWAP_EYEBROW}>{eyebrow}</Eyebrow>
            </Reveal>
          )}
          <Reveal delay={0.1}>
            <p className={`mt-5 font-display text-[2.4rem] font-light leading-[1.05] text-ivory sm:text-6xl ${SWAP_TITLE}`}>{title}</p>
          </Reveal>
          {text && (
            <Reveal delay={0.2}>
              <p className="mt-6 font-display text-2xl italic leading-snug text-[#F6D2BC] [text-shadow:0_1px_14px_rgba(20,16,12,0.5)] sm:text-3xl">{text}</p>
            </Reveal>
          )}
          {note && (
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-ivory/80">{note}</p>
            </Reveal>
          )}
          {children && <Reveal delay={0.35} className="mt-9 flex flex-col gap-3 sm:flex-row">{children}</Reveal>}
        </div>
      </div>
    </section>
  );
}
